import React, { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { db } from '../firebase';
import { collection, updateDoc, doc, onSnapshot, query, orderBy, where, getDocs } from 'firebase/firestore';

const STAGES = [
  { key: 'daftar', label: 'Terdaftar', color: '#94a3b8' },
  { key: 'sembelih', label: 'Disembelih', color: '#dc2626' },
  { key: 'packing', label: 'Dipacking', color: '#d97706' },
  { key: 'distribusi', label: 'Distribusi', color: '#0369a1' },
  { key: 'selesai', label: 'Selesai', color: '#16a34a' },
];

// Mapping status hewan → status distribusi donor (otomatis)
const ANIMAL_TO_DONOR_STATUS = {
  daftar: 'pending',
  sembelih: 'pending',
  packing: 'proses',
  distribusi: 'proses',
  selesai: 'proses',  // distribusi aktual dihandle admin distribusi
};

export default function AdminProcess() {
  const [animals, setAnimals] = useState([]);
  const [donors, setDonors] = useState([]);
  const [toast, setToast] = useState(null);
  const [updating, setUpdating] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  useEffect(() => {
    const u1 = onSnapshot(query(collection(db, 'animals'), orderBy('type', 'asc')), snap =>
      setAnimals(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(collection(db, 'donors'), snap =>
      setDonors(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, []);

  const updateStatus = async (animalId, status) => {
    setUpdating(animalId);
    try {
      // 1. Update status hewan
      await updateDoc(doc(db, 'animals', animalId), {
        status,
        [`${status}At`]: new Date().toISOString()
      });

      // 2. Cari SEMUA donor yang terhubung ke hewan ini (bisa hingga 7 untuk sapi)
      const linkedDonors = donors.filter(d => d.animalId === animalId);
      const newDonorStatus = ANIMAL_TO_DONOR_STATUS[status] || 'pending';

      // 3. Update semua donor yang terhubung secara otomatis
      let updatedCount = 0;
      for (const donor of linkedDonors) {
        const currentOrder = ['pending', 'proses', 'distribusi', 'selesai'].indexOf(donor.distributionStatus || 'pending');
        const newOrder = ['pending', 'proses', 'distribusi', 'selesai'].indexOf(newDonorStatus);
        if (newOrder > currentOrder) {
          await updateDoc(doc(db, 'donors', donor.id), {
            distributionStatus: newDonorStatus,
            updatedAt: new Date().toISOString()
          });
          updatedCount++;
        }
      }

      const stageName = STAGES.find(s => s.key === status)?.label;
      const animal = animals.find(a => a.id === animalId);
      const animalType = animal?.type === 'sapi' ? 'Sapi' : 'Kambing';
      const donorInfo = linkedDonors.length > 0
        ? ` · ${updatedCount} dari ${linkedDonors.length} pengkurban diperbarui`
        : ` · (tidak ada pengkurban terhubung)`;
      showToast(`✅ ${animalType} → ${stageName}${donorInfo}`);
    } catch (e) {
      showToast('❌ Gagal update: ' + e.message, 'error');
    }
    setUpdating(null);
  };

  const sapi = animals.filter(a => a.type === 'sapi');
  const kambing = animals.filter(a => a.type === 'kambing');

  // Semua donor terhubung ke satu hewan (bisa banyak untuk sapi)
  const getDonorsForAnimal = (animalId) => donors.filter(d => d.animalId === animalId);

  const AnimalTable = ({ list, title, icon }) => (
    <div className="card" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--green-900)', marginBottom: 16 }}>{icon} {title} ({list.length} ekor)</h3>
      <div className="table-wrapper" style={{ border: 'none' }}>
        <table>
          <thead>
            <tr><th>Nama Hewan</th><th>Berat</th><th>Pengkurban</th><th>Status Saat Ini</th><th>Update Status</th></tr>
          </thead>
          <tbody>
            {list.map(a => {
              const currentIdx = STAGES.findIndex(s => s.key === a.status);
              const linkedDonors = getDonorsForAnimal(a.id);
              const max = a.type === 'sapi' ? 7 : 1;
              return (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.name}</td>
                  <td style={{ fontSize: 13 }}>{a.weight ? `${a.weight} kg` : '-'}</td>
                  <td>
                    {linkedDonors.length > 0 ? (
                      <div>
                        {linkedDonors.map((d, i) => (
                          <div key={d.id} style={{ fontSize: 12, lineHeight: 1.7 }}>
                            <span style={{ color: 'var(--text-muted)', marginRight: 4 }}>{i + 1}.</span>
                            <span style={{ fontWeight: 600 }}>{d.name}</span>
                          </div>
                        ))}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                          {linkedDonors.length}/{max} slot terisi
                        </div>
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: '#f59e0b' }}>⚠️ Belum ada pengkurban</span>
                    )}
                  </td>
                  <td>
                    <span className="badge" style={{
                      background: STAGES.find(s => s.key === a.status)?.color + '20',
                      color: STAGES.find(s => s.key === a.status)?.color,
                      border: `1px solid ${STAGES.find(s => s.key === a.status)?.color}40`
                    }}>
                      {STAGES.find(s => s.key === a.status)?.label || a.status}
                    </span>
                  </td>
                  <td>
                    {a.status !== 'selesai' ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {STAGES.slice(currentIdx + 1).map(stage => (
                          <button key={stage.key} className="btn btn-sm"
                            style={{ background: stage.color + '15', color: stage.color, border: `1px solid ${stage.color}40`, fontSize: 12 }}
                            onClick={() => updateStatus(a.id, stage.key)}
                            disabled={updating === a.id}>
                            {updating === a.id ? '⏳' : `→ ${stage.label}`}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>✅ Selesai</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>Belum ada data</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Summary tahap
  const summary = STAGES.map(s => ({
    ...s,
    sapi: sapi.filter(a => a.status === s.key).length,
    kambing: kambing.filter(a => a.status === s.key).length,
  }));

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="page-header">
          <h1>Proses Penyembelihan & Packing</h1>
          <p>Perbarui status proses setiap hewan qurban.</p>
        </div>

        {/* Stage summary */}
        <div style={{ display: 'flex', gap: 10, marginBottom: '2rem', flexWrap: 'wrap' }}>
          {summary.map(s => (
            <div key={s.key} style={{ background: '#fff', border: '1px solid var(--border)', borderTop: `3px solid ${s.color}`, borderRadius: 12, padding: '12px 16px', minWidth: 120, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{s.label}</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <div><div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.sapi}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Sapi</div></div>
                <div style={{ width: 1, background: 'var(--border)' }} />
                <div><div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.kambing}</div><div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Kambing</div></div>
              </div>
            </div>
          ))}
        </div>

        <AnimalTable list={sapi} title="Sapi" icon="🐄" />
        <AnimalTable list={kambing} title="Kambing" icon="🐏" />

        {toast && <div className="toast-container"><div className={`toast toast-${toast.type}`}>{toast.type === 'success' ? '✅' : '❌'} {toast.msg}</div></div>}
      </div>
    </div>
  );
}
